import { useEffect, useMemo, useState } from 'react';
import { Copy, CheckCircle2, Info, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/shared/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { type BeltColor } from '@/constants/belt';
import { previewSlotsFromIntervals } from '@/utils/slotCalculation';

interface InstructorStep3Props {
  username: string;
  photoUrl: string | null;
  belt: BeltColor;
  onMarkComplete: () => void | Promise<void>;
}

const DEFAULT_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri'] as const;
const DEFAULT_DURATION = 60;
const CLIPBOARD_TOAST = 'Link copiado para a área de transferência.';

const DEFAULT_INTERVALS = DEFAULT_DAYS.map((day) => ({
  day_of_week: day,
  start_time: '08:00',
  end_time: '17:00',
}));

export const InstructorStep3Agenda = ({
  username,
  photoUrl,
  belt,
  onMarkComplete,
}: InstructorStep3Props) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(true);
  const [createError, setCreateError] = useState<string | null>(null);
  const [slotsPerDay, setSlotsPerDay] = useState<number | null>(null);

  const appBase = import.meta.env.VITE_APP_URL || 'https://app.sossiety.com';
  const publicUrl = `${appBase.replace(/^https?:\/\//, '')}/i/${username}`;
  const fullUrl = `${appBase}/i/${username}`;

  const previewSlotsCount = useMemo(
    () => previewSlotsFromIntervals(DEFAULT_INTERVALS, 'mon', DEFAULT_DURATION).length,
    [],
  );

  const showClipboardToast = () => {
    toast({ description: CLIPBOARD_TOAST });
  };

  // Ensure agenda exists (insert-only; never overwrite server defaults from signup)
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data: existingConfig } = await supabase
          .from('instructor_agenda_config')
          .select('instructor_id, default_duration')
          .eq('instructor_id', user.id)
          .maybeSingle();

        if (!existingConfig) {
          const { error: configErr } = await supabase.from('instructor_agenda_config').insert({
            instructor_id: user.id,
            default_duration: DEFAULT_DURATION,
            is_public: true,
            class_value: null,
            currency: null,
          });
          if (configErr) throw configErr;
        }

        const duration = existingConfig?.default_duration ?? DEFAULT_DURATION;

        const { data: existingIntervals } = await supabase
          .from('instructor_schedule_intervals')
          .select('day_of_week, start_time, end_time')
          .eq('instructor_id', user.id);

        const existingDays = new Set((existingIntervals ?? []).map((r) => r.day_of_week));
        const toInsert = DEFAULT_DAYS.filter((d) => !existingDays.has(d)).map((day) => ({
          instructor_id: user.id,
          day_of_week: day,
          start_time: '08:00:00',
          end_time: '17:00:00',
        }));

        if (toInsert.length > 0) {
          const { error: intErr } = await supabase
            .from('instructor_schedule_intervals')
            .insert(toInsert);
          if (intErr) throw intErr;
        }

        const intervalsForPreview =
          existingIntervals && existingIntervals.length > 0
            ? existingIntervals.map((row) => ({
                day_of_week: row.day_of_week,
                start_time: String(row.start_time).slice(0, 5),
                end_time: String(row.end_time).slice(0, 5),
              }))
            : DEFAULT_INTERVALS;

        const sampleDay =
          intervalsForPreview.find((iv) => iv.day_of_week === 'mon')?.day_of_week ?? 'mon';
        setSlotsPerDay(
          previewSlotsFromIntervals(intervalsForPreview, sampleDay, duration).length ||
            previewSlotsCount,
        );
      } catch (err) {
        setCreateError(err instanceof Error ? err.message : 'Erro ao criar agenda');
        setSlotsPerDay(previewSlotsCount);
      } finally {
        setCreating(false);
      }
    })();
  }, [user, previewSlotsCount]);

  const initials = username?.[0]?.toUpperCase() ?? '?';
  const slotsLabel =
    slotsPerDay != null
      ? `${slotsPerDay} horário${slotsPerDay === 1 ? '' : 's'} disponíve${slotsPerDay === 1 ? 'l' : 'is'}/dia`
      : null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showClipboardToast();
    await onMarkComplete();
  };

  if (creating) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <Loader2 className="w-8 h-8 text-sossiety-red animate-spin" />
        <p className="text-white/50 text-sm">Criando sua agenda...</p>
      </div>
    );
  }

  if (createError) {
    return (
      <div className="space-y-4 text-center py-8">
        <p className="text-red-300 text-sm">{createError}</p>
        <Button
          variant="primary"
          size="md"
          onClick={() => {
            setCreateError(null);
            setCreating(true);
          }}
        >
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h3 className="text-white font-semibold text-base">Sua agenda já está disponível</h3>
        <p className="text-white/50 text-xs leading-relaxed">
          Compartilhe no WhatsApp, Instagram ou na academia. Quem clicar pode solicitar aula direto com você.
        </p>
      </div>

      {/* Profile preview */}
      <div className="flex flex-col items-center gap-3 bg-[#231a1a] border border-[rgba(255,255,255,0.06)] rounded-xl p-4">
        <FramedAvatar
          src={photoUrl ?? undefined}
          initials={initials}
          beltRank={belt}
          frameSize={{ w: 80, h: 88 }}
          size={54}
          isManual
        />
        <div className="text-center space-y-0.5">
          <p className="text-white text-sm font-medium">@{username}</p>
          <span className="inline-flex items-center gap-1 text-[11px] text-sossiety-success font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-sossiety-success" />
            AGENDA ATIVA
          </span>
          {slotsLabel && (
            <p className="text-white/50 text-[11px] pt-1">{slotsLabel}</p>
          )}
        </div>
      </div>

      {/* Shareable URL row */}
      <div className="bg-sossiety-input border border-[rgba(255,255,255,0.1)] rounded-xl p-3 flex items-center justify-between gap-2">
        <span className="font-mono text-white/60 text-xs truncate flex-1">{publicUrl}</span>
        <button
          type="button"
          onClick={handleCopy}
          className={cn(
            'flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
            copied
              ? 'bg-sossiety-success text-sossiety-dark'
              : 'bg-sossiety-red hover:bg-sossiety-red-hover text-white',
          )}
        >
          {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copiado!' : 'Copiar'}
        </button>
      </div>

      {/* Tip */}
      <div className="flex items-start gap-2 bg-[rgba(46,204,113,0.07)] border border-[rgba(46,204,113,0.2)] rounded-xl p-3">
        <Info className="w-3.5 h-3.5 text-sossiety-success mt-0.5 flex-shrink-0" />
        <p className="text-xs text-sossiety-success leading-relaxed">
          <span className="font-bold">Dica:</span> cole este link na bio do seu Instagram ou mande no grupo do WhatsApp da sua academia.
        </p>
      </div>

    </div>
  );
};
